import { useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Trash2, AlertTriangle, Scale, PackageOpen } from "lucide-react";
import { fetcher } from "@/lib/api";
import ContenedoresMap from "@/components/contedores/ContenedoresMap";
import ContenedoresDrawer from "@/components/contedores/ContenedoresDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import "@/i18n/fase9";

const CONCELLOS = ["cambados","a_illa","meano","meis","o_grove","ribadumia","sanxenxo","vilagarcia","vilanova"];

function HeroKpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-white/8 backdrop-blur border border-white/10 px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/60">
        <Icon size={12} strokeWidth={1.6} /> {label}
      </div>
      <div className="font-serif text-xl lg:text-2xl leading-tight mt-1 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

export default function Contenedores() {
  const { t } = useTranslation();
  const [concello, setConcello] = useState("all");
  const [tab, setTab] = useState("map");
  const [selected, setSelected] = useState(null);

  const listQ = useMemo(() => (concello !== "all" ? `?concello=${concello}` : ""), [concello]);
  const { data: cpData, isLoading: cpLoading } = useSWR(`/sigeus/col-points${listQ}`, fetcher);
  const { data: kpi } = useSWR("/sigeus/kpis", fetcher, { refreshInterval: 60000 });
  const { data: weighinsData } = useSWR(tab === "weighins" ? "/sigeus/weighins?limit=100&days=30" : null, fetcher);
  const { data: alarmsData } = useSWR(tab === "alarms" ? "/sigeus/alarms?limit=100&status=all" : null, fetcher);

  const colPoints = cpData?.col_points || [];

  return (
    <div className="min-h-full bg-salnes-ivory" data-testid="contedores-root">
      {/* Hero */}
      <section className="bg-gradient-to-br from-salnes-atlantic via-[#0B4C7A] to-[#082E4B] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 lg:py-14">
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
            {t("fase9.contedores.nav")}
          </div>
          <h1 className="font-serif text-3xl lg:text-5xl leading-tight">
            {t("fase9.contedores.title")}
          </h1>
          <p className="mt-3 text-sm lg:text-base text-white/75 max-w-2xl">
            {t("fase9.contedores.subtitle")}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-8">
            <HeroKpi icon={MapPin} label={t("fase9.contedores.kpi_col_points")} value={kpi?.col_points_active?.toLocaleString()} testid="contedores-kpi-colpoints" />
            <HeroKpi icon={Trash2} label={t("fase9.contedores.kpi_containers")} value={kpi?.containers_total?.toLocaleString()} testid="contedores-kpi-containers" />
            <HeroKpi icon={AlertTriangle} label={t("fase9.contedores.kpi_alarms")} value={kpi?.alarms_open?.toLocaleString()} testid="contedores-kpi-alarms" />
            <HeroKpi icon={Scale} label={t("fase9.contedores.kpi_weighins_today")} value={kpi?.weighins_today?.count?.toLocaleString()} testid="contedores-kpi-today" />
            <HeroKpi icon={PackageOpen} label={t("fase9.contedores.kpi_kg_today")} value={kpi?.weighins_today?.kg ? Math.round(kpi.weighins_today.kg).toLocaleString() : "—"} testid="contedores-kpi-kg-today" />
            <HeroKpi icon={Scale} label={t("fase9.contedores.kpi_ytd_kg")} value={kpi?.ytd_kg_total ? Math.round(kpi.ytd_kg_total).toLocaleString() : "—"} testid="contedores-kpi-ytd" />
          </div>
        </div>
      </section>

      {/* Tabs + Filters */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6">
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <div className="inline-flex rounded-full border hairline bg-white p-1" role="tablist">
            {[
              { k: "map", l: t("fase9.contedores.tab_map") },
              { k: "weighins", l: t("fase9.contedores.tab_weighins") },
              { k: "alarms", l: t("fase9.contedores.tab_alarms") },
            ].map((tb) => (
              <button
                key={tb.k}
                data-testid={`contedores-tab-${tb.k}`}
                onClick={() => setTab(tb.k)}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.16em] rounded-full transition-all ${
                  tab === tb.k ? "bg-salnes-atlantic text-white" : "text-salnes-stone hover:text-salnes-ink"
                }`}
              >
                {tb.l}
              </button>
            ))}
          </div>
          {tab === "map" && (
            <label className="flex items-center gap-2 text-xs text-salnes-stone">
              <span className="uppercase tracking-[0.18em] text-[10px]">{t("fase9.contedores.filter_concello")}</span>
              <select
                data-testid="contedores-filter-concello"
                value={concello}
                onChange={(e) => setConcello(e.target.value)}
                className="bg-white border hairline rounded-lg px-3 py-1.5 text-sm text-salnes-ink focus:outline-none focus:ring-2 focus:ring-salnes-emerald"
              >
                <option value="all">{t("fase9.contedores.filter_all")}</option>
                {CONCELLOS.map((c) => <option key={c} value={c}>{t(`fase2.concello_${c}`, c)}</option>)}
              </select>
            </label>
          )}
          <div className="ml-auto text-xs text-salnes-stone" data-testid="contedores-count">
            {cpData?.count?.toLocaleString() || "—"} / {cpData?.total_active?.toLocaleString() || "—"} activos
          </div>
        </div>

        {tab === "map" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[70vh] rounded-2xl overflow-hidden">
            {cpLoading ? <Skeleton className="w-full h-full" /> : (
              <ContenedoresMap colPoints={colPoints} onSelect={setSelected} viewDetailLabel={t("fase9.contedores.popup_view_detail")} />
            )}
          </motion.div>
        )}

        {tab === "weighins" && (
          <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="contedores-weighins-panel">
            {weighinsData?.weighins?.length ? (
              <table className="w-full text-xs">
                <thead className="bg-salnes-sand/30 text-salnes-stone uppercase tracking-[0.14em] text-[10px]">
                  <tr>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_date")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_point")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_waste")}</th>
                    <th className="px-4 py-3 text-right">{t("fase9.contedores.th_kg")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_vehicle")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_route")}</th>
                  </tr>
                </thead>
                <tbody>
                  {weighinsData.weighins.map((w, i) => (
                    <tr key={i} className="border-t hairline">
                      <td className="px-4 py-2.5 text-salnes-ink">{String(w.weighing_date || "").slice(0, 19)}</td>
                      <td className="px-4 py-2.5">#{w.col_point_id ?? "—"}</td>
                      <td className="px-4 py-2.5">{w.waste_type || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{w.weight_kg?.toLocaleString?.() || "—"}</td>
                      <td className="px-4 py-2.5 font-mono">{w.vehicle_plate || "—"}</td>
                      <td className="px-4 py-2.5">{w.route_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-16 text-center text-sm text-salnes-stone italic">
                {t("fase9.contedores.weighins_empty")}
              </div>
            )}
          </div>
        )}

        {tab === "alarms" && (
          <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="contedores-alarms-panel">
            {alarmsData?.alarms?.length ? (
              <table className="w-full text-xs">
                <thead className="bg-salnes-sand/30 text-salnes-stone uppercase tracking-[0.14em] text-[10px]">
                  <tr>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_opened")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_type")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_priority")}</th>
                    <th className="px-4 py-3 text-left">{t("fase9.contedores.th_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {alarmsData.alarms.map((a) => (
                    <tr key={a.alarm_event_id} className="border-t hairline">
                      <td className="px-4 py-2.5">{String(a.opened_at || "").slice(0, 19)}</td>
                      <td className="px-4 py-2.5">{a.type_name || `#${a.type_id}`}</td>
                      <td className="px-4 py-2.5">{a.priority ?? "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded ${
                          a.status === "open" ? "bg-salnes-coral/10 text-salnes-coral" : "bg-salnes-emerald/10 text-salnes-emerald"
                        }`}>
                          {a.status === "open" ? t("fase9.contedores.status_open") : t("fase9.contedores.status_closed")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-16 text-center text-sm text-salnes-stone italic">
                {t("fase9.contedores.alarms_empty")}
              </div>
            )}
          </div>
        )}
      </section>

      <ContenedoresDrawer colPoint={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
